class MainCollectionComponent extends HTMLElement {
  constructor() {
    super();
    this.gridSelector = "#product-grid";
    this.pageHeight = 0;
    this.lastOpenFacetId = null;
    this.isMobilePanelOpen = false; // Track mobile panel state
    this.clearFiltersClicked = false; // Track if clear filters was clicked
  }

  connectedCallback() {

    // Get configuration from attributes
    this.collectionId = this.getAttribute("collection-id");
    this.templateId = this.getAttribute("template-id") || "category";
    this.currentPage = parseInt(
      window.currentPageFromUrl || this.getAttribute("current-page") || "1",
      10
    );
    this.productsPerPage = parseInt(
      this.getAttribute("products-per-page") || "54",
      10
    );
    this.cardHeight = parseInt(this.getAttribute("card-height") || "563", 10);
    this.gapSize = parseInt(this.getAttribute("gap-size") || "32", 10);

    this.calculatePageHeight();
    this.createInitialSpaceReservation();
    this.loadContent();
  }

  calculatePageHeight() {
    const rowsPerPage = Math.ceil(this.productsPerPage / 3); // Assuming 3 columns
    this.pageHeight =
      rowsPerPage * this.cardHeight + (rowsPerPage - 1) * this.gapSize;
  }

  createInitialLayout() {

    // Reserve space immediately for all pages to prevent layout shift
    this.createInitialSpaceReservation();

    // Create results container for Graphene content
    this.resultsContainer = document.createElement("div");
    this.resultsContainer.className = "graphene-results";
    this.resultsContainer.id = "graphene-results-" + Date.now();
    this.resultsContainer.style.display = "none";
    this.appendChild(this.resultsContainer);
  }

  createInitialSpaceReservation() {
    const totalPages = this.getTotalPages();
    if (!totalPages) {
      console.log(
        "[CollectionComponent] No total pages found - skipping initial space reservation"
      );
      return;
    }

    // Calculate height based on total products, same as individual sentinels
    const totalProducts = totalPages * this.productsPerPage;
    const totalRows = Math.ceil(totalProducts / 3); // 3 columns
    const totalHeight =
      totalRows * this.cardHeight + (totalRows - 1) * this.gapSize;

    // Create a temporary container to reserve space
    this.spaceReservationContainer = document.createElement("div");
    this.spaceReservationContainer.className = "space-reservation";
    this.spaceReservationContainer.style.cssText = `
      height: ${totalHeight}px;
      display: flex;
      position: relative;
    `;

    this.appendChild(this.spaceReservationContainer);
  }

  loadContent() {
    if (!window.$g) {
      console.error("[CollectionComponent] Graphene not available");
      return;
    }

    // Create results container for Graphene content
    this.resultsContainer = document.createElement("div");
    this.resultsContainer.className = "graphene-results";
    this.resultsContainer.id = "graphene-results-" + Date.now();
    this.resultsContainer.style.display = "none";
    this.appendChild(this.resultsContainer);

    const grapheneLangId = this.getAttribute('graphene-lang-id');
    const grapheneCurrencyId = this.getAttribute('graphene-currency-id');

    requestAnimationFrame(() => {
      $g.load({
        template: { id: this.templateId, target: "#" + this.resultsContainer.id },
        page_context: { 
            category_id: this.collectionId,
            language_id: grapheneLangId,
            currency_id: grapheneCurrencyId
        },
        success: () => this.onContentLoaded(),
        error: (err) => {
          console.error("[CollectionComponent] Load failed:", err);
        },
      });
    });
  }

  onContentLoaded() {

    // Show the Graphene results
    this.resultsContainer.style.display = "block";

    // Remove min height to prevent empty space in template grid
    this.resultsContainer.closest('main-collection-component').style.minHeight = 0;

    // Find the grid in the loaded content
    const grid = this.resultsContainer.querySelector(this.gridSelector);

      // Remove the initial space reservation now that we have real content + sentinels
    if (this.spaceReservationContainer) {
      this.spaceReservationContainer.remove();
    }

    // Process stock check elements after content is loaded
    this.processStockCheckElements();

    // Add pagination scroll-to-top functionality
    this.addPaginationScrollListeners();

    this.addFacetDropdownListeners();
    this.addClearFiltersListeners();
    this.addVariantHoverListeners();
    // Populate colour swatches on Graphene cards (combined listings); the module
    // fetches per-product fragments and wires its own hover (collection-card-swatches.js).
    window.SwyftCardSwatches?.enrich(this.resultsContainer);
    this.addAdspotClickListeners();
    this.publishAdspotViewEvents();

    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent("collection-ready", {
        detail: { grid, currentPage: this.currentPage },
      })
    );
  }

  addPaginationScrollListeners() {

    // Find all pagination links in the loaded content
    const paginationWrapper = this.resultsContainer.querySelector(
      ".pagination-wrapper"
    );
    if (paginationWrapper) {
      const paginationLinks = paginationWrapper.querySelectorAll("a");

      paginationLinks.forEach((link) => {
        link.addEventListener("click", (e) => {
          // Add a small delay to allow the page to start loading
          setTimeout(() => {
            window.scrollTo({
              top: 0,
              behavior: "instant",
            });
          }, 500);
        });
      });

    } else {
      console.log("[CollectionComponent] No pagination wrapper found");
    }
  }

  getTotalPages() {
    // First try: Look for the product count data attribute from server
    const productGridElement = document.querySelector("#product-grid");
    if (productGridElement && productGridElement.dataset.pageCount) {
      const totalPages = parseInt(productGridElement.dataset.pageCount, 10);
      return totalPages;
    }

    // Second try: Fallback to URL query string - if we're on page X, assume at least X pages
    const urlParams = new URLSearchParams(window.location.search);
    const pageFromUrl = parseInt(urlParams.get("pg") || "1", 10);
    if (pageFromUrl > 1) {
      return pageFromUrl;
    }

    // Final fallback: Single page
    return 1;
  }

  addFacetDropdownListeners() {

    const container = this.resultsContainer;
    if (!container) return;

    // Desktop dropdown overlay
    let overlay = document.querySelector(".facets__overlay-click");
    if (!overlay) {
      overlay = document.createElement("div");
      overlay.className = "facets__overlay-click";
      overlay.style.cssText = `
        position: fixed;
        inset: 0;
        background: transparent;
        display: none;
        z-index: 2;
      `;
      document.body.appendChild(overlay);
    }

    const openDropdown = (item) => {
      container
        .querySelectorAll(".facets__bar-item.is-open")
        .forEach((el) => el.classList.remove("is-open"));
      item.classList.add("is-open");
      overlay.style.display = "block";
      this.lastOpenFacetId = item.dataset.facet; // ✅ remember which was open
    };

    const closeAll = (force = false) => {
      // Don't close dropdowns unless forced (e.g., by escape key or overlay click)
      if (!force && this.lastOpenFacetId) {
        return;
      }
      container
        .querySelectorAll(".facets__bar-item.is-open")
        .forEach((el) => el.classList.remove("is-open"));
      overlay.style.display = "none";
      this.lastOpenFacetId = null;
    };

    // Desktop dropdown handlers
    container.querySelectorAll(".facets__bar-button").forEach((btn) => {
      btn.type = "button";
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        const barItem = btn.closest(".facets__bar-item");
        const alreadyOpen = barItem.classList.contains("is-open");
        alreadyOpen ? closeAll(true) : openDropdown(barItem);
      });
    });

    overlay.addEventListener("click", () => closeAll(true));
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeAll(true);
    });

    // Mobile panel handlers
    this.addMobilePanelListeners();

    // Add clear filters listeners
    this.addClearFiltersListeners();

    // Add variant hover listeners
    this.addVariantHoverListeners();

    // restore previously open facet after re-render
    // if (this.lastOpenFacetId) {
    //   const item = container.querySelector(
    //     `.facets__bar-item[data-facet="${this.lastOpenFacetId}"]`
    //   );
    //   if (item) {
    //     item.classList.add("is-open");
    //     overlay.style.display = "block";
    //   }
    // }

  }

  addMobilePanelListeners() {

    // Find mobile filter button
    const mobileFilterButton = document.querySelector(".facets__mobile-filter");
    if (!mobileFilterButton) {
      return;
    }

    // Find mobile panel elements
    const panel = document.querySelector(".facets__panel");
    const panelOverlay = document.querySelector(".facets__overlay");
    const closeButton = document.querySelector(".facets__close");
    const applyButton = document.querySelector("button.apply-filters");
    const clearButton = document.querySelector("button.clear-results");

    if (!panel || !panelOverlay || !closeButton || !applyButton) {
      console.log("[CollectionComponent] Mobile panel elements not found");
      return;
    }

    const openPanel = () => {
      panelOverlay.classList.add("is-visible");
      panel.classList.add("is-open");
      panel.setAttribute("aria-hidden", "false");
      document.body.classList.add("no-scroll");
      mobileFilterButton.blur();
      this.isMobilePanelOpen = true; // Remember panel is open
    };

    const closePanel = () => {
      panelOverlay.classList.remove("is-visible");
      panel.classList.remove("is-open");
      panel.setAttribute("aria-hidden", "true");
      document.body.classList.remove("no-scroll");
      this.isMobilePanelOpen = false; // Remember panel is closed
    };

    // Event listeners
    mobileFilterButton.addEventListener("click", (e) => {
      e.preventDefault();
      openPanel();
    });

    if (closeButton) {
      closeButton.addEventListener("click", (e) => {
        e.preventDefault();
        closePanel();
      });
    }

    if (applyButton) {
      applyButton.addEventListener("click", (e) => {
        e.preventDefault();
        closePanel();
      });
    }

    if (clearButton) {
      clearButton.addEventListener("click", (e) => {
        e.preventDefault();
        closePanel();
      });
    }

    panelOverlay.addEventListener("click", closePanel);

    // Prevent scroll on overlay touch (iOS Safari fix)
    panelOverlay.addEventListener(
      "touchmove",
      (e) => {
        e.preventDefault();
      },
      { passive: false }
    );

    // Allow scrolling within the panel content
    const panelContent = panel.querySelector(".facets__accordion");
    if (panelContent) {
      panelContent.addEventListener(
        "touchmove",
        (e) => {
          e.stopPropagation(); // Don't let it bubble up to overlay
        },
        { passive: true }
      );
    }

    // Escape key to close
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && panel.classList.contains("is-open")) {
        closePanel();
      }
    });

    // restore mobile panel state after re-render (unless clear filters was clicked)
    if (this.isMobilePanelOpen && !this.clearFiltersClicked) {
      panelOverlay.classList.add("is-visible");
      panel.classList.add("is-open");
      panel.setAttribute("aria-hidden", "false");
      document.body.classList.add("no-scroll");
    } else if (this.clearFiltersClicked) {
      this.isMobilePanelOpen = false; // Reset panel state
      this.clearFiltersClicked = false; // Reset clear filters flag
    }

  }

  addClearFiltersListeners() {

    const container = this.resultsContainer;
    if (!container) return;

    // Find all clear filters links (both desktop and mobile)
    const clearLinks = container.querySelectorAll(".clear-results");
    
    clearLinks.forEach(link => {
      link.addEventListener("click", (e) => {
        this.clearFiltersClicked = true;
        this.isMobilePanelOpen = false; // Close mobile panel
        this.lastOpenFacetId = null; // Close desktop dropdowns
      });
    });

    console.log(`[CollectionComponent] Added listeners to ${clearLinks.length} clear filter links.`);
  }

  addVariantHoverListeners() {

    const container = this.resultsContainer;
    if (!container) return;

    // Iterate over each product card within the results container
    container.querySelectorAll('.grid__item .product-card-wrapper').forEach(function (productCard) {
      // Select the product image within the product card
      const productImage = productCard.querySelector('.card__media .media img');

      if (!productImage) return;

      // Save the original srcset and src as data attributes on the image element
      if (!productImage.hasAttribute('data-original-srcset')) {
        productImage.setAttribute('data-original-srcset', productImage.srcset);
      }

      if (!productImage.hasAttribute('data-original-src')) {
        productImage.setAttribute('data-original-src', productImage.src);
      }

      const variantPillsContainer = productCard.querySelector('.variant__pills');

      if (variantPillsContainer) {
        variantPillsContainer.querySelectorAll('.variant__pill').forEach(function (pill) {
          pill.addEventListener('mouseenter', function () {
            const newSrcSet = pill.querySelector('span').getAttribute('data-srcset');
            const newSrc = pill.querySelector('span').getAttribute('data-src');

            if (newSrcSet && newSrc) {
              // Preload and decode the image first
              const preloadImg = new Image();
              preloadImg.onload = function() {
                // Use decode() if available for smoother switching
                if (preloadImg.decode) {
                  preloadImg.decode().then(() => {
                    productImage.srcset = newSrcSet;
                    productImage.src = newSrc;
                  }).catch(() => {
                    // Fallback if decode fails
                    productImage.srcset = newSrcSet;
                    productImage.src = newSrc;
                  });
                } else {
                  // Fallback for browsers without decode support
                  productImage.srcset = newSrcSet;
                  productImage.src = newSrc;
                }
              };
              preloadImg.srcset = newSrcSet;
              preloadImg.src = newSrc;
            }
          });
        });

        variantPillsContainer.addEventListener('mouseleave', function () {
          // Roll back to the original srcset and src from the data attributes
          const originalSrcSet = productImage.getAttribute('data-original-srcset');
          const originalSrc = productImage.getAttribute('data-original-src');
          
          if (originalSrcSet && originalSrc) {
            productImage.srcset = originalSrcSet;
            productImage.src = originalSrc;
          }
        });
      }
    });

  }

  // Fire custom event to track adspot clicks
  addAdspotClickListeners() {
    if (!Shopify?.analytics) return;

    const adspots = this.resultsContainer.querySelectorAll('.adspot__link-override');
    if (!adspots.length) return;

    adspots.forEach((link) => {
      link.addEventListener('click', () => {
        window.Shopify?.analytics?.publish?.('adspot_click', {
          url: link.href,
          label: link.textContent.trim(),
        });
      });
    });
  }

  // Fire custom event to track adspot impressions, once per adspot per render
  publishAdspotViewEvents() {
    if (!Shopify?.analytics) return;

    const adspots = this.resultsContainer.querySelectorAll('.adspot:not([data-view-fired])');
    if (!adspots.length) return;

    adspots.forEach((adspot) => {
      const link = adspot.querySelector('.adspot__link-override');
      if (!link) return;
      adspot.setAttribute('data-view-fired', '');
      window.Shopify?.analytics?.publish?.('adspot_view', {
        url: link.href,
        label: link.textContent.trim(),
      });
    });
  }

  // Method to process all stock-check elements and update display
  processStockCheckElements() {
    const stockCheckElements = this.querySelectorAll('.stock-check');
    
    stockCheckElements.forEach(element => {
      // A model card has no stock of its own; collection-card-swatches.js works its line out from the colours.
      const deliveryMessage = element.dataset.modelMessage || getFormattedAvailabilityDate(element);
      if (deliveryMessage) {
        // Show the element and add the message
        element.style.display = '';
        element.classList.toggle('stock-check--christmas', deliveryMessage === getCollectionDeliveryMessages()?.christmas?.text);
        element.setAttribute('data-delivery-message', deliveryMessage);
        element.innerHTML = deliveryMessage;
      } else {
        // Hide the element if no next day delivery is available
        element.style.display = 'none';
        element.innerHTML = '';
        element.removeAttribute('data-delivery-message');
      }
    });
  }
}

// Register the custom element
customElements.define("main-collection-component", MainCollectionComponent);

// Global function to get formatted availability date (for use in HTML/onclick)
window.getAvailabilityDate = function(stockCheckElement) {
  if (typeof stockCheckElement === 'string') {
    stockCheckElement = document.querySelector(stockCheckElement);
  }
  return getFormattedAvailabilityDate(stockCheckElement);
};

// expose to the global scope so inline onclick can find it
window.addToCart = function (submitButton, variantId, quantity = 1) {
  const guard = window.swyftStockGuard;

  if (guard && !guard.begin(variantId)) return;

  const verdict = guard && guard.allowsAdd({ button: submitButton, variantId, quantity });
  if (verdict && !verdict.allowed) {
    guard.end(variantId);
    guard.flash(submitButton, guard.messageFor(verdict.stockQty));
    return;
  }

  const cart =
    document.querySelector("cart-notification") ||
    document.querySelector("cart-drawer");

  const config =
    typeof fetchConfig === "function"
      ? fetchConfig("javascript")
      : { method: "POST", headers: { "X-Requested-With": "XMLHttpRequest" } };

  if (config.headers) delete config.headers["Content-Type"];

  const formData = new FormData();
  formData.append("id", variantId);
  formData.append("quantity", quantity);

  if (cart && typeof cart.getSectionsToRender === "function") {
    formData.append(
      "sections",
      cart.getSectionsToRender().map((s) => s.id)
    );
    formData.append("sections_url", window.location.pathname);
    if (typeof cart.setActiveElement === "function") {
      cart.setActiveElement(document.activeElement);
    }
  }

  config.body = formData;

  submitButton.setAttribute("aria-disabled", "true");
  submitButton.classList.add("loading");
  submitButton.querySelector(".loading__spinner")?.classList.remove("hidden");

  fetch("/cart/add.js", config)
    .then((r) => {
      if (!r.ok) throw new Error(`Error adding to cart: ${r.statusText}`);
      return r.json();
    })
    .then((cartItem) => {
      submitButton.setAttribute("aria-disabled", "false");
      submitButton.classList.remove("loading");
      submitButton.querySelector(".loading__spinner")?.classList.add("hidden");
      
      // Publish cart update event for tracking
      if (typeof publish === 'function') {
        // Fetch the updated cart to get the full cart data
        fetch('/cart.js')
          .then(r => r.json())
          .then(cartData => {
            publish(PUB_SUB_EVENTS.cartUpdate, { 
              source: 'collection-add-to-cart', 
              cartData: cartData,
              variantId: variantId 
            });
          })
          .catch(err => console.error('[CollectionComponent] Failed to fetch cart for tracking:', err));
      }
      
      // Only call renderContents if it exists
      cart?.renderContents?.(cartItem);
    })
    .catch((err) => {
      console.error("Error:", err);
      submitButton.setAttribute("aria-disabled", "false");
      submitButton.classList.remove("loading");
      submitButton.querySelector(".loading__spinner")?.classList.add("hidden");
    })
    .finally(() => {
      window.swyftStockGuard?.end(variantId);
    });
};

// Read the translated delivery messages injected by theme.liquid
function getCollectionDeliveryMessages() {
  if (typeof getCollectionDeliveryMessages.cache !== 'undefined') {
    return getCollectionDeliveryMessages.cache;
  }
  try {
    const element = document.querySelector('[data-delivery-messages]');
    getCollectionDeliveryMessages.cache = element
      ? JSON.parse(element.textContent || '{}')
      : null;
  } catch (err) {
    getCollectionDeliveryMessages.cache = null;
  }
  return getCollectionDeliveryMessages.cache;
}

// Utility function to check if next day delivery is available
function formatAvailabilityDate(dateString) {
  if (!dateString) return null;

  try {
    const availableDate = new Date(dateString);
    if (isNaN(availableDate.getTime())) return null;

    // Get today's date (without time component)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Set available date to start of day for comparison
    availableDate.setHours(0, 0, 0, 0);

    // If available date is today or earlier, show next day delivery message
    if (availableDate <= today) {
      const messages = getCollectionDeliveryMessages();
      return messages?.next_day_available || 'Available next day';
    }
    
    // Same switch, wording and last date as the product page (Theme settings > Christmas delivery); dispatched before the last date arrives by it.
    const christmas = getCollectionDeliveryMessages()?.christmas;
    const lastDate = christmas?.last_date ? new Date(`${christmas.last_date}T00:00:00`) : null;
    if (christmas?.text && lastDate && !isNaN(lastDate) && availableDate < lastDate) {
      return christmas.text;
    }

    // If available date is in the future, show nothing
    return null;
  } catch (error) {
    console.error('[CollectionComponent] Error checking availability date:', error);
    return null;
  }
}

// Function to extract and format first available date from stock-check element
function getFormattedAvailabilityDate(stockCheckElement) {
  if (!stockCheckElement) return null;
  
  try {
    // Get the date string directly from the data-stock-check attribute
    const dateString = stockCheckElement.getAttribute('data-stock-check');
    const stockInventory = stockCheckElement.getAttribute('data-inventory');
    
    if (!stockInventory || stockInventory == '0') {
        return null;
    }

    if (!dateString) {
      console.log('[CollectionComponent] No stock check data found');
      return null;
    }
    
    return formatAvailabilityDate(dateString);
  } catch (error) {
    console.error('[CollectionComponent] Error parsing stock check data:', error);
    return null;
  }
}
